<template>
  <div
    class="fixed inset-0 backdrop-blur-[2px] bg-[rgba(20,17,11,0.8)] lg:hidden z-50 transition-opacity duration-300"
    aria-hidden="true"
    :class="isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'"
    @click="isOpen = !isOpen"
  />
  <div class="w-full text-base">
    <!-- Current Nav Tab -->
    <div
      class="cursor-pointer bg-container-bg rounded-xl p-3 w-full flex flex-col items-center relative gap-2 lg:h-16"
      @click="isOpen = true"
    >
      <!-- top-level tab -->
      <div
        class="font-semibold bg-tab-bg-selected rounded-full p-2.5 w-full flex flex-row gap-2.5 items-center justify-start h-full"
      >
        <Icon
          v-if="activeTabObj.activeTab?.icon || (props.useActiveIcon && activeTabObj.activeTab)"
          :name="activeTabObj.activeTab?.icon || props.activeIcon"
          class="w-5 h-5"
        />
        <span class="text-lg font-medium">
          {{ activeTabObj.activeTab ? activeTabObj.activeTab.label : "" }}
        </span>
        <div class="grow" />
        <div class="h-full p-2 flex items-center justify-self-end">
          <Icon name="ChevronDown" class="w-4 h-4 ml-2 transition min-w-4" />
        </div>
      </div>
      <!-- subtab, if applicable -->
      <div class="w-full pl-12" v-if="activeTabObj.activeSubTab">
        <div class="absolute left-8 w-5 h-8 overflow-hidden">
          <div class="absolute bottom-0 border-2 border-outline-darker w-16 h-16 rounded-xl"></div>
        </div>
        <div
          v-if="activeTabObj.activeSubTab"
          class="font-semibold bg-tab-bg-selected rounded-full p-2 w-full flex flex-row gap-2.5 items-center justify-start"
        >
          <Icon
            v-if="
              activeTabObj.activeSubTab?.icon || (props.useActiveIcon && activeTabObj.activeSubTab)
            "
            :name="activeTabObj.activeSubTab?.icon || props.activeIcon"
            class="w-5 h-5"
          />
          <span class="text-lg font-medium">
            {{ activeTabObj.activeSubTab ? activeTabObj.activeSubTab.label : "" }}
          </span>
          <div class="grow" />
          <div class="h-full p-2 flex items-center justify-self-end">
            <Icon name="ChevronDown" class="w-4 h-4 ml-2 transition min-w-4" />
          </div>
        </div>
      </div>
    </div>
    <!-- Drawer with subnav -->
    <div
      class="fixed bottom-0 left-0 z-50 w-screen h-auto bg-bg-canvas flex-col transition-all duration-500 rounded-t-2xl"
      :class="
        isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none translate-y-full'
      "
    >
      <div class="w-full flex flex-row justify-between items-center p-4">
        <div class="flex flex-col semibold">
          <div class="text-sm text-strong flex flex-row gap-2">
            <Icon name="Search" class="w-4 h-4" />
            <span>Profile</span>
          </div>
          <div class="text-xl text-strongest">Select profile category</div>
        </div>
        <div>
          <Button @click="isOpen = false" variant="paper-primary" size="sm">
            <template #icon> <Icon name="X" class="h-4 w-4 fill-outline-darker" /></template>
          </Button>
        </div>
      </div>
      <div class="w-full border-b border-outline-dividers"></div>
      <SubNavigation
        ref="subnav"
        :tabs="props.tabs"
        :active-tab="props.activeTab"
        @update:activeTab="$emit('update:activeTab', $event)"
        @tab-action="handleAction"
        :use-active-icon="props.useActiveIcon"
        :active-icon="props.activeIcon"
        vertical
        class="p-4"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Button, SubNavigation, Icon } from "@/components";

// props
const props = defineProps({
  tabs: {
    type: Array,
    default: [],
  },
  activeTab: {
    type: String,
    default: "",
  },
  useActiveIcon: {
    type: Boolean,
    default: false,
  },
  activeIcon: {
    type: String,
    default: "Sticker",
  },
});

const subnav = ref(null);
const isOpen = ref(false); // whether the nav sheet is open

// returns active tab object to determine what to show when sheet is closed
const activeTabObj = computed(() => {
  return setActiveTabObj();
});

// watch on activeTab
// if the active tab is a subtab, open the parent tab menu
// in a watch because TabView sets the activeTab when it mounts, which is after this component is mounted
watch(
  () => props.activeTab,
  (newVal) => {
    // when activeTab prop changes, check activeTabObj and update openTab
    const { activeTab, activeSubTab } = activeTabObj.value;
    if (activeSubTab) {
      subnav.value.openTab = activeTab.key;
    } else {
      subnav.value.openTab = null; // close all
    }
  }
);

// find the active tab (and possibly subtab) objects based on activeTab prop
function setActiveTabObj() {
  let activeTab = null;
  let activeSubTab = null;

  //console.log("setting active tab obj for:", props.activeTab);
  props.tabs.forEach((tab) => {
    if (tab.key === props.activeTab) {
      activeTab = tab;
    } else if (tab.subtabs) {
      tab.subtabs.forEach((subtab) => {
        if (subtab.key === props.activeTab) {
          activeTab = tab;
          activeSubTab = subtab;
        }
      });
    }
  });
  return { activeTab, activeSubTab };
}

const emit = defineEmits(["update:activeTab", "tab-action"]);

function handleAction(payload) {
  isOpen.value = false; // close drawer
  emit("tab-action", payload);
}

defineExpose({
  isOpen,
});
</script>
